<template>
    <LoaderSplash :loaded="loaded">
        <b-container v-if="loaded">
            <b-row>
                <b-col>
                    <h1>
                        Edit {{ event.name }}
                    </h1>

                    <b-form @submit.prevent="onSubmit">
                        <b-form-group>
                            <label>
                                Client
                            </label>

                            <b-form-select 
                                v-model="form.client_id"
                                :options="client_options"
                                class=mb-2>
                                
                                <template slot="first">
                                    <option :value="null" disabled>None</option>
                                </template>
                                
                            </b-form-select>

                            <label>
                                Event Name
                            </label>

                            <b-form-input
                                type="text"
                                v-model="form.name"
                                required
                                placeholder="Event name">    
                            </b-form-input>

                            <b-form-checkbox class="my-3" v-model="form.requires_payment" switch>
                                Is this a paid event? ({{ form.requires_payment ? "Yes" : "No" }})
                            </b-form-checkbox>

                            <div class="my-3" v-show="form.requires_payment">
                                <label>
                                    Cost
                                </label>

                                <b-form-input
                                    type="number"
                                    v-model="form.cost"
                                    step=0.01
                                    required>    
                                </b-form-input>

                                <b-form-checkbox v-model="form.requires_payment_upfront" switch>
                                    Does this event require payment upfront? ({{ form.requires_payment_upfront ? "Yes" : "No" }})
                                </b-form-checkbox>
                            </div>

                            <label>
                                Description
                            </label>

                            <vue-editor
                                    v-model="form.description"
                                    :editorToolbar="
                                    [
                                        ['bold', 'italic', 'underline'],
                                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                        ['code-block']
                                  ]"></vue-editor>

                        </b-form-group>

                        <b-form-group>
                            <b-row>
                                <b-col>
                                    <span class="float-left">
                                        <label>
                                            Event image
                                        </label>

                                        <image-picker
                                            :current_image_url="form.image"
                                            :height="image_dimensions.large.height"
                                            :width="image_dimensions.large.width"
                                            @image-picked="(file) => { form.image_base64 = file; }">
                                        </image-picker>
                                    </span>
                                </b-col>
                            </b-row>
                        </b-form-group>

                        <b-form-group>
                            <b-row>
                                <b-col>
                                    <address-selector 
                                        :address-object="addressObject"
                                        @changed="addressChanged"></address-selector>
                                </b-col>
                            </b-row>
                        </b-form-group>

                        <b-button class="mt-3" type="submit" variant="primary">
                            Save
                        </b-button>
                    </b-form>

                    <div class="my-5">
                        <h3 class="mb-3">
                            Questions
                        </h3>

                        <editable-question-list 
                            :event-id="event.id"
                            :questions="event.questions"
                            @changed="questions => { this.questions = questions; }">
                        </editable-question-list>
                    </div>

                    <hr>

                    <div class="my-5">
                        <h3>
                            Itinerary
                        </h3>

                        <itinerary-form :event="event" :days="event.days"></itinerary-form>
                    </div>
                </b-col>
            </b-row>
        </b-container>
    </LoaderSplash>
</template>

<script>
    import moment from 'moment';
    import { VueEditor } from "vue2-editor";
    import datePicker from 'vue-bootstrap-datetimepicker';
    import ImagePicker from '@/components/ImagePicker.vue';
    import AddressSelector from '@/components/AddressSelector.vue';
    import EditableQuestionList from '@/components/EditableQuestionList.vue';
    import ItineraryForm from '@/components/ItineraryForm.vue';
    import clientRepository from "@/repository/clients";
    import eventRepository from "@/repository/events";

    let date_format = "DD-MM-YYYY HH:mm";

    export default {
        components: {
            ImagePicker,
            datePicker,
            AddressSelector,
            EditableQuestionList,
            ItineraryForm,
            VueEditor
        },

        props: ['id'],

        data() {
            return {
                is_paid_event: false,
                event: null,
                client: null,
                clients: [],
                questions: [],
                image_dimensions: {
                    large: {
                        height: 400,
                        width: 800
                    },
                    small: {
                        height: 200,
                        width: 300
                    },
                },
                errors: {},
                loaded: false,
                form: null
            }
        },

        computed: {
            client_options() {
                return this.clients.map((client) => {
                    return {
                        value: client.id,
                        text: client.name
                    }
                });
            },

            addressObject() {
                return {
                    address: this.form.address_address,
                    lat: this.form.address_lat,
                    lng: this.form.address_lng
                }
            }
        },

        methods: {
            addressChanged(address) {
                this.form.address_address = address.address;
                this.form.address_lat = address.lat;
                this.form.address_lng = address.lng;
            },

            onSubmit() {
                eventRepository.update(this.form)
                    .then(response => {
                        this.$notify({
                            group: 'main',
                            type: 'success',
                            title: 'Client Edited',
                            text: 'New client successfully edited.'
                        });
                    }).catch(error => {
                        let e = error.response.data.errors;

                        let errors = Object.keys(e).map(key => {
                            return e[key].join("<br/>");
                        }).join("<br/>");

                        this.$notify({
                            group: 'main',
                            type: 'error',
                            title: 'Form Errors',
                            text: errors,
                            duration: 10000,
                            width: '500px'
                        });
                    });
            }
        },

        mounted() {
            eventRepository.getById(
                this.id,
                {
                    include: ['client', 'questions', 'days.itinerary_items']
                }
            ).then(response => {
                this.event = response.data;
                this.client = response.data.client;
                this.form = { 
                    ...this.event
                };
                this.loaded = true;
            });

            clientRepository.getAll()
                .then(response => {
                    this.clients = response.data;
                });
        }
    }
</script>

<style scoped lang="scss">
    
</style>